import { computed, effect, signal } from '@preact/signals';

/** O que a pessoa escolheu: seguir o aparelho ("sistema") ou fixar claro/escuro. */
export type Preferencia = 'sistema' | 'claro' | 'escuro';
/** O tema que de fato aparece na tela. */
export type Tema = 'claro' | 'escuro';

export const CHAVE_TEMA = 'mapas.tema';
/** Cor da barra do navegador/aparelho (meta theme-color) em cada tema. Igual ao --bg de styles.css. */
export const COR_BARRA: Record<Tema, string> = { escuro: '#0f0e0d', claro: '#f4f3f0' };

export const ROTULO_PREFERENCIA: Record<Preferencia, string> = { sistema: 'Automático', claro: 'Claro', escuro: 'Escuro' };
export const ORDEM_PREFERENCIA: readonly Preferencia[] = ['sistema', 'claro', 'escuro'];

/** Regra única: preferência explícita vence; "sistema" segue o aparelho. */
export function resolverTema(pref: Preferencia, sistemaEscuro: boolean): Tema {
  if (pref === 'claro') return 'claro';
  if (pref === 'escuro') return 'escuro';
  return sistemaEscuro ? 'escuro' : 'claro';
}

export function preferenciaValida(v: unknown): v is Preferencia {
  return v === 'sistema' || v === 'claro' || v === 'escuro';
}

function lerPreferencia(): Preferencia {
  try {
    const v = localStorage.getItem(CHAVE_TEMA);
    return preferenciaValida(v) ? v : 'sistema';
  } catch {
    return 'sistema'; // armazenamento bloqueado: segue o aparelho
  }
}

const consulta = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;

export const preferencia = signal<Preferencia>(lerPreferencia());
const sistemaEscuro = signal(consulta ? consulta.matches : true);
/** Tema em uso. Quem lê `tema.value` num componente é redesenhado sozinho quando ele muda (mapa, legenda…). */
export const tema = computed<Tema>(() => resolverTema(preferencia.value, sistemaEscuro.value));

export function definirPreferencia(p: Preferencia) {
  preferencia.value = p;
  try {
    if (p === 'sistema') localStorage.removeItem(CHAVE_TEMA);
    else localStorage.setItem(CHAVE_TEMA, p);
  } catch {
    /* sem armazenamento: vale só nesta visita */
  }
}

/** Liga o tema ao documento. Chamar uma vez, antes de desenhar o app. */
export function iniciarTema() {
  consulta?.addEventListener('change', (e) => (sistemaEscuro.value = e.matches));
  // Outra aba do mesmo navegador mudou a escolha: acompanha.
  window.addEventListener('storage', (e) => {
    if (e.key === CHAVE_TEMA) preferencia.value = lerPreferencia();
  });
  effect(() => {
    const t = tema.value;
    document.documentElement.dataset.tema = t;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', COR_BARRA[t]);
  });
}
